import type { TabKey } from '@/models/tab-registry';
import { clearStoredSnapshot } from '@/models/tab-snapshot';
import { ComponentPortal } from '@angular/cdk/portal';
import { computed, effect, untracked } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { v4 as uuid } from 'uuid';

export type Orientation = 'horizontal' | 'vertical';

export type LayoutNode = TabGroupNode | SplitNode;

export interface TabGroupNode {
  id: string;
  type: 'group';
  tabs: Tab[];
  activeTabId: string;
}

export interface SplitNode {
  id: string;
  type: 'split';
  orientation: Orientation;
  children: LayoutNode[];
}

export interface LayoutRoot {
  activeGroupId: string;
  layout: SplitNode | TabGroupNode | null;
}

export interface Tab {
  id: string;
  icon: string;
  title: string;
  content: ComponentPortal<unknown>;
  /** Identifies the hosted component in the stored layout. */
  componentKey: TabKey;
  /** Handed to the component through `TabData`. Persisted, so it must be plain JSON. */
  data?: unknown;
}

interface TabState {
  root: LayoutRoot;
  layoutStack: LayoutRoot[];
  stackIndex: number;
  sizes: Record<string, number[]>; // Store the sizes of each split node, because they should not be in the stack
}

const initialState: TabState = {
  root: { layout: null, activeGroupId: '' },
  layoutStack: [],
  stackIndex: 1,
  sizes: {},
};

export function cloneLayoutNode(node: null): null;
export function cloneLayoutNode(node: LayoutNode): LayoutNode;
export function cloneLayoutNode(node: LayoutNode | null): LayoutNode | null;
export function cloneLayoutNode(node: LayoutNode | null): LayoutNode | null {
  if (node === null) {
    return null;
  }
  if (node.type === 'group') {
    // shallow copy the group, but re-use the same portal refs
    return {
      id: node.id,
      type: 'group',
      activeTabId: node.activeTabId,
      tabs: node.tabs.map((t) => ({ ...t })), // content ref is carried over
    };
  }

  // split node: copy sizes & ids, recurse into children
  return {
    id: node.id,
    type: 'split',
    orientation: node.orientation,
    children: node.children.map((item) => cloneLayoutNode(item)).filter((c): c is LayoutNode => c !== null),
  };
}

/** Keeps the preferred group while it survives pruning, else the first group that did, else none. */
export function resolveActiveGroupId(layout: LayoutNode | null, preferred: string): string {
  let firstGroupId = '';

  const findPreferred = (node: LayoutNode): boolean => {
    if (node.type === 'group') {
      if (!firstGroupId) {
        firstGroupId = node.id;
      }
      return node.id === preferred;
    }
    return node.children.some(findPreferred);
  };

  if (layout && findPreferred(layout)) {
    return preferred;
  }

  return firstGroupId;
}

let skipOnce = false;

export const TabStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(() => ({
    findLayoutNode: (base: LayoutNode | null, id: string): LayoutNode | null => {
      const findNode = (node: LayoutNode): LayoutNode | null => {
        if (node.id === id) {
          return node;
        }

        if (node.type === 'split') {
          for (const child of node.children) {
            const found = findNode(child);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };

      if (!base) {
        return null;
      }

      return findNode(base);
    },
    _getAllLayoutNodes: (base: LayoutNode | null): LayoutNode[] => {
      const nodes: LayoutNode[] = [];
      const listNodes = (node: LayoutNode): void => {
        nodes.push(node);
        if (node.type === 'split') {
          for (const child of node.children) {
            listNodes(child);
          }
        }
      };
      if (!base) {
        return [];
      }
      listNodes(base);
      return nodes;
    },
    cleanLayoutState: (base: LayoutNode | null): LayoutNode | null => {
      // Remove empty groups and splits. If nothing left, return initial state. Do it recursively.
      // internal recursive helper that returns null when a node should be removed
      const prune = (node: LayoutNode): LayoutNode | null => {
        if (node.type === 'group') {
          // drop any empty tab‐group
          if (node.tabs.length === 0) {
            return null;
          }
          // keep non‐empty groups as‐is
          return { ...node };
        }

        // node.type === 'split'
        // try to clean each child
        const kept = node.children
          .map((child) => {
            const pruned = prune(child);
            return pruned ? { node: pruned } : null;
          })
          .filter(<T>(x: T | null): x is T => !!x);

        // if no children survive, drop this split
        if (kept.length === 0) {
          return null;
        }

        // if only one child survives, collapse the split
        if (kept.length === 1) {
          return kept[0].node;
        }

        const children = kept.map(({ node }) => node);

        return {
          ...node,
          children,
        };
      };

      if (!base) {
        return null;
      }

      return prune(base);
    },
  })),
  withMethods(({ root, layoutStack, ...state }) => ({
    reset: () => {
      patchState(state, initialState);
      // Both logout and the 401 path come through here, so this is what stops a stored layout
      // leaking entity names to the next user.
      clearStoredSnapshot();
    },
    /**
     * Replaces the layout with one rebuilt from storage.
     *
     * Leaves the undo stack alone on purpose: this runs before the first change detection, so the
     * effect below records the restored root as the base of a fresh stack. Seeding it here as well
     * would push twice.
     */
    restore: (root: LayoutRoot, sizes: Record<string, number[]>) => {
      patchState(state, { root, sizes });
    },
    switchTab: (group: TabGroupNode, tab: Tab) => {
      const base = cloneLayoutNode(root().layout);
      const node = state.findLayoutNode(base, group.id);

      if (node?.type !== 'group') {
        return;
      }

      node.activeTabId = tab.id;

      patchState(state, { root: { layout: base, activeGroupId: group.id } });
    },
    resize: (splitId: string, newSizes: number[]) => {
      patchState(state, { sizes: { ...state.sizes(), [splitId]: newSizes } });
    },
    closeTab: (group: TabGroupNode | null, tab: Tab) => {
      const base = cloneLayoutNode(root().layout);

      let node: LayoutNode | undefined | null = null;
      if (group) {
        node = state.findLayoutNode(base, group.id);
      } else {
        const allNodes = state._getAllLayoutNodes(base);
        if (!allNodes || !allNodes.length) return;
        node = allNodes.find((n) => n.type === 'group' && n.tabs.find((t) => t.id === tab.id));
      }

      if (node?.type !== 'group') {
        return;
      }

      const origIndex = node.tabs.findIndex((t) => t.id === tab.id);

      node.tabs = node.tabs.filter((t) => t.id !== tab.id);

      if (node.activeTabId === tab.id) {
        const afterRemoval = node.tabs;
        let newIndex = origIndex;
        if (newIndex >= afterRemoval.length) {
          newIndex = afterRemoval.length - 1;
        }
        node.activeTabId = afterRemoval[newIndex]?.id || '';
      }

      // clean up empty groups and splits
      const cleaned = state.cleanLayoutState(base);

      patchState(state, {
        root: { layout: cleaned, activeGroupId: resolveActiveGroupId(cleaned, root().activeGroupId) },
      });
    },
    moveTab: (original: TabGroupNode, tab: Tab, target: TabGroupNode, index: number) => {
      const base = cloneLayoutNode(root().layout);
      const origNode = state.findLayoutNode(base, original.id);
      const targetNode = state.findLayoutNode(base, target.id);

      if (origNode?.type !== 'group' || targetNode?.type !== 'group') {
        return;
      }

      // remove tab from original group
      origNode.tabs = origNode.tabs.filter((t) => t.id !== tab.id);

      // add tab to target group
      targetNode.tabs.splice(index, 0, tab);

      // clean up empty groups and splits
      const cleaned = state.cleanLayoutState(base);

      patchState(state, {
        root: { layout: cleaned, activeGroupId: target.id },
      });
    },

    closeAllTabs: (group: TabGroupNode) => {
      const base = cloneLayoutNode(root().layout);
      const node = state.findLayoutNode(base, group.id);

      if (node?.type !== 'group') {
        return;
      }

      node.tabs = [];

      // clean up empty groups and splits
      const cleaned = state.cleanLayoutState(base);

      patchState(state, { root: { layout: cleaned, activeGroupId: '' } });
    },

    closeOtherTabs: (group: TabGroupNode, tab: Tab) => {
      const base = cloneLayoutNode(root().layout);
      const node = state.findLayoutNode(base, group.id);

      if (node?.type !== 'group') {
        return;
      }

      node.tabs = node.tabs.filter((t) => t.id === tab.id);
      node.activeTabId = tab.id;

      // clean up empty groups and splits
      const cleaned = state.cleanLayoutState(base);

      patchState(state, { root: { layout: cleaned, activeGroupId: group.id } });
    },

    split: (base: TabGroupNode, tab: Tab, orientation: Orientation) => {
      // Replace the current group with a split node, with the 'tab' on the right and the remaining tabs on the left
      const origIndex = base.tabs.findIndex((t) => t.id === tab.id);
      const afterRemoval = base.tabs.filter((t) => t.id !== tab.id);
      let newIndex = origIndex;
      if (newIndex >= afterRemoval.length) {
        newIndex = afterRemoval.length - 1;
      }

      const newGroup: TabGroupNode = {
        id: base.id,
        type: 'group',
        tabs: base.tabs.filter((t) => t.id !== tab.id),
        activeTabId: afterRemoval[newIndex]?.id || '',
      };

      const newGroupId = uuid();

      const newSplit: SplitNode = {
        id: uuid(),
        type: 'split',
        orientation,
        children: [
          newGroup,
          {
            id: newGroupId,
            type: 'group',
            tabs: [tab],
            activeTabId: tab.id,
          },
        ],
      };

      patchState(state, {
        sizes: { ...state.sizes(), [newSplit.id]: [0.5, 0.5] },
      });

      // Update the layout state
      const updatedLayout = cloneLayoutNode(root().layout);
      const node = state.findLayoutNode(updatedLayout, base.id);
      if (node?.type === 'group') {
        Object.assign(node, newSplit);
      }

      // Clean up empty groups and splits
      const cleaned = state.cleanLayoutState(updatedLayout);
      patchState(state, {
        root: { layout: cleaned, activeGroupId: newGroupId },
      });
    },

    addTabToActiveGroup: (tab: Tab) => {
      if (!root().layout) {
        // Create a new root node if layout is null
        const newGroup: TabGroupNode = {
          id: uuid(),
          type: 'group',
          tabs: [tab],
          activeTabId: tab.id,
        };

        patchState(state, {
          root: { layout: newGroup, activeGroupId: newGroup.id },
        });

        return;
      }

      // In some cases the activeGroupId does not name a group of its own, e.g. when the last active
      // set was all closed, or when closing a split group took the active group with it.
      const active = resolveActiveGroupId(root().layout, root().activeGroupId);

      const base = cloneLayoutNode(root().layout);
      const node = state.findLayoutNode(base, active);

      if (node?.type !== 'group') {
        return;
      }

      // Add the new tab to the active group
      node.tabs.push(tab);

      node.activeTabId = tab.id;
      // The group the tab landed in is the active one - leaving an id behind that names no group
      // would keep determineActiveTab() empty, so the new tab would open without being active.
      patchState(state, {
        root: { layout: base, activeGroupId: active },
      });
    },

    addAndSplitTab: (tab: Tab, orientation: Orientation) => {
      const base = cloneLayoutNode(root().layout);

      if (!base) {
        return;
      }

      // Replace the root node with a split node
      const newSplit: SplitNode = {
        id: uuid(),
        type: 'split',
        orientation,
        children: [
          base,
          {
            id: uuid(),
            type: 'group',
            tabs: [tab],
            activeTabId: tab.id,
          },
        ],
      };

      patchState(state, {
        sizes: { ...state.sizes(), [newSplit.id]: [0.5, 0.5] },
      });

      // Update the layout state
      const updatedLayout = cloneLayoutNode(base);
      Object.assign(updatedLayout, newSplit);
      const cleaned = state.cleanLayoutState(updatedLayout);

      patchState(state, {
        root: { layout: cleaned, activeGroupId: newSplit.children[1].id },
      });
    },

    markGroupAsActive: (group: TabGroupNode) => {
      // If the groupId is not already active, update the state
      if (root.activeGroupId() !== group.id) {
        patchState(state, {
          root: { layout: root().layout, activeGroupId: group.id },
        });
      }
    },

    determineActiveTab: () => {
      return computed(() => {
        const base = root().layout;
        if (!base) {
          return null;
        }

        const node = state.findLayoutNode(base, root().activeGroupId);
        if (node?.type !== 'group') {
          return null;
        }

        const activeTab = node.tabs.find((t) => t.id === node.activeTabId);
        if (!activeTab) {
          return null;
        }

        return {
          ...activeTab,
          content: activeTab.content,
        };
      });
    },

    goBack: () => {
      const stack = layoutStack();
      if (stack.length === 0) {
        return;
      }

      skipOnce = true;

      const newItem = stack[stack.length - 2];

      if (!newItem) {
        patchState(state, initialState);
        return;
      }

      patchState(state, { root: newItem, layoutStack: stack.slice(0, -1) });
    },
  })),
  withHooks(({ root, layoutStack, ...state }) => ({
    onInit() {
      effect(() => {
        const currentRoot = root();

        if (!currentRoot) {
          return;
        }

        if (skipOnce) {
          skipOnce = false;
          return;
        }

        const copy = {
          layout: cloneLayoutNode(currentRoot.layout),
          activeGroupId: currentRoot.activeGroupId,
        };

        untracked(() => {
          patchState(state, {
            layoutStack: [...layoutStack(), copy],
            stackIndex: layoutStack().length + 1,
          });
        });
      });
    },
  })),
);
